import { Stack } from '@startuptools/ui/Stack';
import { Children, ComponentProps, ReactElement, Suspense, useState } from 'react';
import { Sheet } from '@startuptools/ui/Sheet';
import { StepperProvider } from '../common/StepperContext';
import { compact, isArray } from 'lodash-es';
import { StepperStepProvider } from '../common/StepperStepContext';
import { StepperStepContentProvider } from '../common/StepperStepContentContext';
import { StateStepperStep } from './StateStepperStep';

export type Child = ReactElement<ComponentProps<typeof StateStepperStep>> | false | undefined;

const getChildrenArray = (children: Child | Child[]) => {
  if (!children) return [];
  return isArray(children) ? compact(children) : [children];
};

export const StateStepper = ({
  children,
  onStepSaved,
  onFinalStep,
}: {
  children: Child | Child[];
  onStepSaved: (id: string) => Promise<unknown> | void;
  onFinalStep: () => Promise<unknown> | void;
}) => {
  const childrenArr = getChildrenArray(children);
  const [currentStep, setCurrentStep] = useState(childrenArr[0]!.props.id ?? 'initial');

  const stepNext = async (id: string) => {
    await onStepSaved(id);
    const currentChildIdx = childrenArr.findIndex(c => currentStep.endsWith(c.props.id));
    const nextChild = childrenArr[currentChildIdx + 1];
    if (nextChild) {
      return setCurrentStep(nextChild.props.id);
    }
    await onFinalStep();
  };

  const shownChild = childrenArr.find(c => c.props.id === currentStep);

  return (
    <Stack direction="row" gap={2} sx={{ width: '100%' }}>
      <StepperProvider stepNext={() => stepNext(currentStep)} stepTo={setCurrentStep} currentStep={currentStep}>
        <Sheet
          variant="outlined"
          sx={{
            width: '40%',
            maxWidth: '270px',
            minWidth: '200px',
            borderRadius: 6,
            paddingY: 1.5,
            flexShrink: 0,
          }}
        >
          <Stack direction="column">
            {Children.map(childrenArr, (c, i) => (
              <StepperStepProvider stepNumber={`${i + 1}`}>{c}</StepperStepProvider>
            ))}
          </Stack>
        </Sheet>

        <Sheet
          variant="outlined"
          sx={{
            width: '60%',
            flexGrow: 1,
            borderRadius: 6,
            padding: 2,
          }}
        >
          {shownChild && (
            <Suspense key={shownChild.props.id}>
              <StepperStepContentProvider key={shownChild.props.id} validatorStatus={shownChild.props.validatorStatus}>
                {shownChild.props.children}
              </StepperStepContentProvider>
            </Suspense>
          )}
        </Sheet>
      </StepperProvider>
    </Stack>
  );
};
